/**
 * Translations for Coming Soon Page
 * Supports English (en) and Spanish (es)
 */

export type Language = 'en' | 'es'

export interface ComingSoonTranslations {
  hero: {
    title: string
    subtitle: string
    comingSoon: string
  }
  valueProposition: {
    title: string
    auctions: {
      title: string
      description: string
    }
    buyingSelling: {
      title: string
      description: string
    }
    enterpriseRfqs: {
      title: string
      description: string
    }
    governmentContracting: {
      title: string
      description: string
    }
    targetAudience: {
      title: string
      manufacturers: {
        title: string
        description: string
      }
      distributors: {
        title: string
        description: string
      }
      epcs: {
        title: string
        description: string
      }
    }
  }
  partnership: {
    title: string
    subtitle: string
    collaborativeDevelopment: {
      title: string
      description: string
    }
    directCollaboration: {
      title: string
      description: string
    }
    featureRequests: {
      title: string
      description: string
    }
    adaptiveSystem: {
      title: string
      description: string
    }
    cta: {
      title: string
      description: string
      button: string
      subtext: string
    }
  }
  contact: {
    title: string
    subtitle: string
    form: {
      companyName: string
      contactName: string
      email: string
      message: string
      sendMessage: string
      sending: string
      messageSent: string
      orEmail: string
    }
  }
  footer: {
    tagline: string
    copyright: string
  }
}

const translations: Record<Language, ComingSoonTranslations> = {
  en: {
    hero: {
      title: "EvoltBay is Building the Next Layer of Solar Energy Ecommerce",
      subtitle: "Leading solar ecommerce platform and enterprise quotation system for solar panels, solar inverters, solar batteries, and complete solar systems. We're extending your existing solar ecosystem—not replacing it.",
      comingSoon: "Coming Soon"
    },
    valueProposition: {
      title: "What EvoltBay Enables",
      auctions: {
        title: "Auctions",
        description: "Participate in competitive bidding for solar equipment and maximize value for your business."
      },
      buyingSelling: {
        title: "Buying & Selling",
        description: "Connect with verified buyers and sellers in the solar industry marketplace."
      },
      enterpriseRfqs: {
        title: "Enterprise RFQs",
        description: "Streamline your request-for-quotation process with our enterprise quotation system."
      },
      governmentContracting: {
        title: "Government Contracting",
        description: "Access government solar contracting opportunities and certified vendor networks."
      },
      targetAudience: {
        title: "Built for Solar Industry Leaders",
        manufacturers: {
          title: "Manufacturers",
          description: "Reach new markets and expand distribution"
        },
        distributors: {
          title: "Distributors",
          description: "Connect with buyers and manage inventory"
        },
        epcs: {
          title: "EPCs & Enterprise Buyers",
          description: "Streamline procurement and project management"
        }
      }
    },
    partnership: {
      title: "Built Through Partnership",
      subtitle: "EvoltBay treats solar companies as strategic partners, not just customers. We collaborate directly with partners to build a platform that extends your ecosystem.",
      collaborativeDevelopment: {
        title: "Collaborative Development",
        description: "Partner companies work directly with the EvoltBay development team to shape the platform. You can request new features, suggest workflow improvements, and influence system behavior to match your specific business needs. This ensures EvoltBay evolves with you, reflecting real operational requirements rather than forcing you to adapt to a rigid system."
      },
      directCollaboration: {
        title: "Direct Collaboration",
        description: "Work directly with our development team to shape features and workflows."
      },
      featureRequests: {
        title: "Feature Requests",
        description: "Suggest improvements and new capabilities that align with your operations."
      },
      adaptiveSystem: {
        title: "Adaptive System",
        description: "The platform adapts to your processes, reducing friction and accelerating innovation."
      },
      cta: {
        title: "Interested in Becoming a Partner?",
        description: "Partner companies benefit from faster innovation, reduced friction, and a system that adapts to existing processes instead of forcing change. Whether you're a manufacturer, distributor, or enterprise buyer, we're building EvoltBay to extend your ecosystem—not replace it.",
        button: "Contact Us at support@evoltbay.com",
        subtext: "Reach out to learn more about partnership opportunities, early collaboration, or to discuss how EvoltBay can extend your solar operations."
      }
    },
    contact: {
      title: "Get Notified When We Launch",
      subtitle: "Interested in learning more? Send us a message and we'll keep you updated.",
      form: {
        companyName: "Company Name",
        contactName: "Contact Name",
        email: "Email",
        message: "Message",
        sendMessage: "Send Message",
        sending: "Sending...",
        messageSent: "Message Sent!",
        orEmail: "Or email us directly at"
      }
    },
    footer: {
      tagline: "Extending solar ecosystems — not replacing them.",
      copyright: "© {year} EvoltBay. All rights reserved."
    }
  },
  es: {
    hero: {
      title: "EvoltBay está Construyendo la Próxima Capa de Comercio Electrónico de Energía Solar",
      subtitle: "Plataforma líder de comercio solar y sistema de cotización empresarial para paneles solares, inversores solares, baterías solares y sistemas solares completos. Estamos extendiendo tu ecosistema solar existente, no reemplazándolo.",
      comingSoon: "Próximamente"
    },
    valueProposition: {
      title: "Lo que EvoltBay Habilita",
      auctions: {
        title: "Subastas",
        description: "Participa en licitaciones competitivas para equipos solares y maximiza el valor para tu negocio."
      },
      buyingSelling: {
        title: "Compra y Venta",
        description: "Conéctate con compradores y vendedores verificados en el mercado de la industria solar."
      },
      enterpriseRfqs: {
        title: "RFQs Empresariales",
        description: "Optimiza tu proceso de solicitud de cotización con nuestro sistema de cotización empresarial."
      },
      governmentContracting: {
        title: "Contratación Gubernamental",
        description: "Accede a oportunidades de contratación solar gubernamental y redes de proveedores certificados."
      },
      targetAudience: {
        title: "Construido para Líderes de la Industria Solar",
        manufacturers: {
          title: "Fabricantes",
          description: "Alcanza nuevos mercados y expande la distribución"
        },
        distributors: {
          title: "Distribuidores",
          description: "Conéctate con compradores y gestiona inventario"
        },
        epcs: {
          title: "EPCs y Compradores Empresariales",
          description: "Optimiza la adquisición y gestión de proyectos"
        }
      }
    },
    partnership: {
      title: "Construido a Través de Asociaciones",
      subtitle: "EvoltBay trata a las empresas solares como socios estratégicos, no solo como clientes. Colaboramos directamente con los socios para construir una plataforma que extiende tu ecosistema.",
      collaborativeDevelopment: {
        title: "Desarrollo Colaborativo",
        description: "Las empresas socias trabajan directamente con el equipo de desarrollo de EvoltBay para dar forma a la plataforma. Puedes solicitar nuevas funciones, sugerir mejoras en los flujos de trabajo e influir en el comportamiento del sistema para que coincida con las necesidades específicas de tu negocio. Esto asegura que EvoltBay evolucione contigo, reflejando requisitos operativos reales en lugar de forzarte a adaptarte a un sistema rígido."
      },
      directCollaboration: {
        title: "Colaboración Directa",
        description: "Trabaja directamente con nuestro equipo de desarrollo para dar forma a funciones y flujos de trabajo."
      },
      featureRequests: {
        title: "Solicitudes de Funciones",
        description: "Sugiere mejoras y nuevas capacidades que se alineen con tus operaciones."
      },
      adaptiveSystem: {
        title: "Sistema Adaptativo",
        description: "La plataforma se adapta a tus procesos, reduciendo la fricción y acelerando la innovación."
      },
      cta: {
        title: "¿Interesado en Convertirte en Socio?",
        description: "Las empresas socias se benefician de una innovación más rápida, menos fricción y un sistema que se adapta a los procesos existentes en lugar de forzar cambios. Ya seas fabricante, distribuidor o comprador empresarial, estamos construyendo EvoltBay para extender tu ecosistema, no reemplazarlo.",
        button: "Contáctanos en support@evoltbay.com",
        subtext: "Comunícate para obtener más información sobre oportunidades de asociación, colaboración temprana o para discutir cómo EvoltBay puede extender tus operaciones solares."
      }
    },
    contact: {
      title: "Recibe Notificación Cuando Lancemos",
      subtitle: "¿Interesado en saber más? Envíanos un mensaje y te mantendremos informado.",
      form: {
        companyName: "Nombre de la Empresa",
        contactName: "Nombre de Contacto",
        email: "Correo Electrónico",
        message: "Mensaje",
        sendMessage: "Enviar Mensaje",
        sending: "Enviando...",
        messageSent: "¡Mensaje Enviado!",
        orEmail: "O escríbenos directamente a"
      }
    },
    footer: {
      tagline: "Extendiendo ecosistemas solares — no reemplazándolos.",
      copyright: "© {year} EvoltBay. Todos los derechos reservados."
    }
  }
}

/**
 * Get translations for a specific language
 */
export function getComingSoonTranslations(lang: Language): ComingSoonTranslations {
  return translations[lang] || translations.en
}

/**
 * Detect user's preferred language from browser
 */
export function detectLanguage(): Language {
  if (typeof window === 'undefined') return 'en'
  
  // Check localStorage first (user preference)
  const savedLang = localStorage.getItem('coming-soon-language') as Language | null
  if (savedLang && (savedLang === 'en' || savedLang === 'es')) {
    return savedLang
  }
  
  // Check browser language
  const browserLang = navigator.language || (navigator as any).userLanguage || 'en'
  if (browserLang.startsWith('es')) {
    return 'es'
  }
  
  return 'en'
}

/**
 * Save language preference
 */
export function saveLanguagePreference(lang: Language): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('coming-soon-language', lang)
  }
}
